import { type FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type RootState } from '@editor2/reducers/root';
import { pageGroupPage } from '@editor2/containers/PageGroupPage/styles';
import Header from '@editor2/components/Header';
import PageGroupEditorForm from './components/PageGroupEditorForm';
import PagesList from './components/PagesList';
import Tools from './components/Tools';
import { Nav, Tab } from 'react-bootstrap';
import UnsavedChangesTooltip from '@editor2/components/UnsavedChangesTooltip';
import {
	checkUnsavedConfigChanges,
	checkUnsavedTocChanges
} from '@editor2/services/unsavedChangesService';
import { updateActiveTab } from '@editor2/reducers/editor/editorSlice';
import { useConnectSocket } from '@editor2/hooks/useConnectSocket';
import { useTabTitle } from '@editor2/hooks/useTabTitle';
import { sanitizeCourseId } from '@editor2/services/helpersService';
import Navigation from '@editor2/components/Header/Navigation';
import RoutingCheckWrapper from '../App/RoutingCheckWrapper';
import { useSettingsMode } from '@editor2/hooks/useSettingsMode';
import SideOptionsPanel from '../App/SideOptionsPanel';

interface Props {
	match: {
		params: {
			courseId: string;
			chapterId: string;
			pageGroupId: string;
		};
	};
}

const PageGroupPage: FC<Props> = (props) => {
	const { match } = props;
	const { courseId: courseIdParam, chapterId, pageGroupId } = match.params;
	const courseId = sanitizeCourseId(courseIdParam);

	const dispatch = useDispatch();

	const { course, chapters, page_groups, changes, settingsMode, activeTab } = useSelector(
		(state: RootState) => state.courses[courseId]
	);
	const chapter = chapters && chapters.entities[chapterId];
	const pageGroup = page_groups && page_groups.entities[pageGroupId];

	const [key, setKey] = useState(activeTab.page_groups || 'toc');

	useConnectSocket({ roomName: `page_group.${pageGroupId}`, courseId });
	useTabTitle({ title: `${course.name}: ${pageGroup.name}` });

	const onTabChange = (key: string) => {
		setKey(key);
		dispatch(updateActiveTab({ courseId, key: 'page_groups', activeTab: key }));
	};

	useEffect(() => {
		if (checkUnsavedConfigChanges(changes)) setKey('settings');
		if (key === 'settings' && checkUnsavedTocChanges(changes) && !settingsMode) setKey('toc');
	}, [changes]);

	useSettingsMode({ settingsMode, setKey });

	return (
		pageGroup && (
			<RoutingCheckWrapper courseId={courseId}>
				<div className={pageGroupPage}>
					<Header course={course} chapter={chapter} pageGroup={pageGroup} />
					<SideOptionsPanel courseId={courseId} />
					<Navigation courseId={courseId} chapter={chapter} pageGroup={pageGroup} />

					<Tab.Container
						id=""
						activeKey={key}
						onSelect={(k) => onTabChange(k)}
						unmountOnExit={true}>
						<Nav variant="tabs" className="tocTabs">
							<UnsavedChangesTooltip condition={checkUnsavedConfigChanges(changes) || settingsMode}>
								<Nav.Item>
									<Nav.Link
										eventKey="pageGroup"
										disabled={checkUnsavedConfigChanges(changes) || settingsMode}>
										Properties
									</Nav.Link>
								</Nav.Item>
							</UnsavedChangesTooltip>

							<UnsavedChangesTooltip condition={checkUnsavedConfigChanges(changes) || settingsMode}>
								<Nav.Item>
									<Nav.Link
										eventKey="toc"
										disabled={checkUnsavedConfigChanges(changes) || settingsMode}>
										Contents
									</Nav.Link>
								</Nav.Item>
							</UnsavedChangesTooltip>
						</Nav>

						<Tab.Content>
							<Tab.Pane eventKey="pageGroup">
								<PageGroupEditorForm pageGroup={pageGroup} courseId={courseId} key={pageGroupId} />
							</Tab.Pane>

							<Tab.Pane eventKey="toc">
								<Tools pageGroupId={pageGroupId} courseId={courseId} />
								<PagesList pageGroupId={pageGroupId} chapterId={chapterId} courseId={courseId} />
							</Tab.Pane>
						</Tab.Content>
					</Tab.Container>
				</div>
			</RoutingCheckWrapper>
		)
	);
};

export default PageGroupPage;
